import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 pb-6 border-b-2 border-gray-200">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 italic">Effective Date: January 2025</p>
        </header>

        <main className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              GUARDLY ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our platform and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              1. Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Name and contact information (email, phone number, address)</li>
              <li>Account credentials (username and password)</li>
              <li>Payment and billing information</li>
              <li>Government-issued identification (for Security Service Providers)</li>
              <li>Professional licenses and certifications (for Security Service Providers)</li>
              <li>Profile information and photos</li>
              <li>Communication preferences</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.2 Information Collected Automatically</h3>
            <p className="text-gray-700 leading-relaxed mb-4">When you use our Services, we automatically collect:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Device information (type, operating system, unique identifiers)</li>
              <li>Location data (with your permission)</li>
              <li>Usage information (pages viewed, features used, time spent)</li>
              <li>IP address and browser information</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1.3 Information from Third Parties</h3>
            <p className="text-gray-700 leading-relaxed mb-4">We may receive information about you from:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Background check providers (for Security Service Providers)</li>
              <li>Payment processors</li>
              <li>Social media platforms (if you connect your account)</li>
              <li>Other users who provide information about you</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              2. How We Use Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use collected information to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Provide, maintain, and improve our Services</li>
              <li>Process transactions and send related information</li>
              <li>Send technical notices, updates, and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Verify identity and conduct background checks</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address security issues</li>
              <li>Comply with legal obligations</li>
              <li>Send marketing communications (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              3. How We Share Your Information
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 With Service Providers</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We share information with third-party service providers who perform services on our behalf, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Payment processing</li>
              <li>Email delivery</li>
              <li>Background checks</li>
              <li>Analytics services</li>
              <li>Customer support</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 With Other Users</h3>
            <p className="text-gray-700 leading-relaxed">
              When you book services, limited information is shared between Customers and Security Service Providers to facilitate the service.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may disclose information if required by law or if we believe disclosure is necessary to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Comply with legal obligations</li>
              <li>Protect our rights and property</li>
              <li>Prevent fraud or security issues</li>
              <li>Protect the safety of users or the public</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              4. Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your information for as long as necessary to provide our Services and comply with legal obligations. 
              Retention periods vary based on the type of information and purpose of collection.
            </p>
            
            <div className="overflow-x-auto mt-6">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                <caption className="sr-only">Data retention periods</caption>
                <thead className="bg-primary-600">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Data Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Retention Period
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Account Information</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Duration of account plus 3 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Transaction Records</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">7 years for tax compliance</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Background Checks</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">As required by applicable law</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Marketing Preferences</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Until you unsubscribe</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              5. Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational measures to protect your information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
              <p className="text-gray-700">
                <strong>Note:</strong> While we strive to protect your information, no method of transmission over the internet is 100% secure. 
                We cannot guarantee absolute security.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              6. Your Rights and Choices
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.1 Access and Update</h3>
            <p className="text-gray-700 leading-relaxed">
              You can access and update your account information through your profile settings.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.2 Communications Preferences</h3>
            <p className="text-gray-700 leading-relaxed mb-4">You can opt-out of marketing communications by:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Clicking "unsubscribe" in emails</li>
              <li>Updating notification preferences in your account</li>
              <li>Contacting us directly</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.3 Location Data</h3>
            <p className="text-gray-700 leading-relaxed">
              You can enable or disable location services through your device settings.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.4 Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              Most browsers allow you to control cookies through settings. Note that disabling cookies may affect functionality.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">6.5 Account Deletion</h3>
            <p className="text-gray-700 leading-relaxed">
              You can request account deletion by contacting us. Some information may be retained as required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              7. California Privacy Rights
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              California residents have additional rights under the California Consumer Privacy Act (CCPA), including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to request deletion of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@guardly.app" className="text-primary-600 hover:text-primary-700">privacy@guardly.app</a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              8. International Data Transfers
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for international transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              9. Children's Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our Services are not directed to children under 18. We do not knowingly collect information from children. 
              If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              10. Third-Party Links
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our Services may contain links to third-party websites. We are not responsible for the privacy practices of these sites. 
              We encourage you to read their privacy policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              11. Updates to This Policy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy periodically. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Posting the updated policy on our platform</li>
              <li>Updating the "Effective Date" at the top</li>
              <li>Sending email notification for significant changes</li>
            </ul>
          </section>

          <section className="mb-12 bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-1 h-6 bg-primary-600 mr-3 rounded"></span>
              12. Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <address className="not-italic space-y-2">
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:privacy@guardly.app" className="text-primary-600 hover:text-primary-700">privacy@guardly.app</a>
              </p>
              <p className="text-gray-700">
                <strong>General Inquiries:</strong> <a href="mailto:contact@guardly.app" className="text-primary-600 hover:text-primary-700">contact@guardly.app</a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong> <a href="https://www.guardly.app" className="text-primary-600 hover:text-primary-700">www.guardly.app</a>
              </p>
            </address>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Data Protection Officer</h3>
            <p className="text-gray-700">
              For privacy-specific concerns, you can contact our Data Protection Officer at{' '}
              <a href="mailto:dpo@guardly.app" className="text-primary-600 hover:text-primary-700">dpo@guardly.app</a>
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;